import React, { useState } from "react";
import { Workflow } from "../../types/workflow";
import { useMobile } from "../../@/hooks/use-mobile";
import WorkflowEditModal from "./WorkflowEditModal";
import { workflowService } from "../../services/workflowService";

interface WorkflowListProps {
  workflows: Workflow[];
  onWorkflowUpdate: () => void;
  onCreateNew: () => void;
}

const WorkflowList: React.FC<WorkflowListProps> = ({
  workflows,
  onWorkflowUpdate,
  onCreateNew,
}) => {
  const [selectedWorkflow, setSelectedWorkflow] = useState<Workflow | null>(
    null
  );
  const [showEditModal, setShowEditModal] = useState(false);
  const isMobile = useMobile();

  const handleDeleteWorkflow = async (workflowId: string) => {
    await workflowService.deleteWorkflow(workflowId);
    setShowEditModal(false);
    setSelectedWorkflow(null);
    onWorkflowUpdate();
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case "active":
        return "bg-green-100 text-green-800";
      case "paused":
        return "bg-yellow-100 text-yellow-800";
      case "draft":
        return "bg-gray-100 text-gray-800";
      case "failed":
        return "bg-red-100 text-red-800";
      case "completed":
        return "bg-blue-100 text-blue-800";
      default:
        return "bg-gray-100 text-gray-800";
    }
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleString();
  };

  const calculateSuccessRate = (workflow: Workflow) => {
    if (workflow.execution_count === 0) return 0;
    return Math.round(
      (workflow.success_count / workflow.execution_count) * 100
    );
  };

  if (workflows.length === 0) {
    return (
      <div className="text-center py-12">
        <div className="mx-auto w-24 h-24 bg-gray-100 rounded-full flex items-center justify-center mb-4">
          <svg
            className="w-12 h-12 text-gray-400"
            fill="none"
            stroke="currentColor"
            viewBox="0 0 24 24"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={2}
              d="M9 5H7a2 2 0 00-2 2v10a2 2 0 002 2h8a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2"
            />
          </svg>
        </div>
        <h3 className="text-lg font-medium text-gray-900 mb-2">
          No workflows yet
        </h3>
        <p className="text-gray-500 mb-6 max-w-sm mx-auto">
          Get started by creating your first workflow to automate your processes
        </p>
        <button
          onClick={onCreateNew}
          className="px-4 py-2 bg-indigo-600 text-white text-sm font-medium rounded-md hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:ring-offset-2"
        >
          Create Your First Workflow
        </button>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div
        className={`flex ${
          isMobile ? "flex-col space-y-3" : "items-center justify-between"
        }`}
      >
        <div>
          <h2
            className={`${
              isMobile ? "text-lg" : "text-xl"
            } font-semibold text-gray-900`}
          >
            My Workflows
          </h2>
          <p className="text-sm text-gray-600">
            {workflows.length} workflow{workflows.length !== 1 ? "s" : ""}{" "}
            configured
          </p>
        </div>
        <button
          onClick={onCreateNew}
          className={`px-4 py-2 bg-indigo-600 text-white text-sm font-medium rounded-md hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:ring-offset-2 ${
            isMobile ? "w-full sm:w-auto" : ""
          }`}
        >
          Create New Workflow
        </button>
      </div>

      {/* Mobile Card Layout */}
      {isMobile ? (
        <div className="space-y-4">
          {workflows.map((workflow) => (
            <div
              key={workflow.id}
              className="bg-white border border-gray-200 rounded-lg p-4 shadow-sm cursor-pointer"
              onClick={() => {
                setSelectedWorkflow(workflow);
                setShowEditModal(true);
              }}
            >
              <div className="flex items-start justify-between mb-3">
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2">
                    <h3 className="text-base font-medium text-gray-900 truncate">
                      {workflow.name}
                    </h3>
                    {workflow.is_vc_managed && (
                      <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-purple-100 text-purple-800 flex-shrink-0">
                        VC Managed
                      </span>
                    )}
                  </div>
                  <p className="text-sm text-gray-500 mt-1 truncate">
                    {workflow.description || "No description"}
                  </p>
                </div>
                <span
                  className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${getStatusColor(
                    workflow.status
                  )} ml-2 flex-shrink-0`}
                >
                  {workflow.status}
                </span>
              </div>

              <div className="grid grid-cols-2 gap-4 text-sm">
                <div>
                  <span className="text-gray-500">Executions:</span>
                  <span className="ml-1 font-medium">
                    {workflow.execution_count}
                  </span>
                </div>
                <div>
                  <span className="text-gray-500">Success Rate:</span>
                  <span className="ml-1 font-medium">
                    {calculateSuccessRate(workflow)}%
                  </span>
                </div>
                <div className="col-span-2">
                  <span className="text-gray-500">Last Run:</span>
                  <span className="ml-1 text-xs">
                    {workflow.last_executed_at
                      ? formatDate(workflow.last_executed_at)
                      : "Never"}
                  </span>
                </div>
              </div>

              <div className="mt-4 flex flex-wrap gap-2">
                <button
                  className="px-3 py-1 bg-gray-50 text-gray-700 text-sm rounded-md hover:bg-gray-100 flex-1 sm:flex-none"
                  onClick={(e) => {
                    e.stopPropagation();
                    setSelectedWorkflow(workflow);
                    setShowEditModal(true);
                  }}
                >
                  Edit
                </button>
              </div>
            </div>
          ))}
        </div>
      ) : (
        /* Desktop Table Layout */
        <div className="bg-white shadow-sm border border-gray-200 rounded-lg">
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Workflow
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Status
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Executions
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Success Rate
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Last Run
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Actions
                  </th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {workflows.map((workflow) => (
                  <tr
                    key={workflow.id}
                    className="hover:bg-gray-50 cursor-pointer"
                    onClick={() => {
                      setSelectedWorkflow(workflow);
                      setShowEditModal(true);
                    }}
                  >
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div>
                        <div className="flex items-center gap-2">
                          <span className="text-sm font-medium text-gray-900">
                            {workflow.name}
                          </span>
                          {workflow.is_vc_managed && (
                            <span
                              className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-purple-100 text-purple-800"
                              title="This workflow is managed by your VC and cannot be paused or deleted"
                            >
                              VC Managed
                            </span>
                          )}
                        </div>
                        <div className="text-sm text-gray-500 max-w-xs truncate">
                          {workflow.description || "No description"}
                        </div>
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <span
                        className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${getStatusColor(
                          workflow.status
                        )}`}
                      >
                        {workflow.status}
                      </span>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                      {workflow.execution_count}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                      {calculateSuccessRate(workflow)}%
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                      {workflow.last_executed_at
                        ? formatDate(workflow.last_executed_at)
                        : "Never"}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm font-medium space-x-2">
                      <button
                        className="text-indigo-600 hover:text-indigo-900"
                        onClick={(e) => {
                          e.stopPropagation();
                          setSelectedWorkflow(workflow);
                          setShowEditModal(true);
                        }}
                      >
                        Edit
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* Edit Workflow Modal */}
      {showEditModal && selectedWorkflow && (
        <WorkflowEditModal
          workflow={selectedWorkflow}
          mode="edit"
          onClose={() => setShowEditModal(false)}
          onUpdated={onWorkflowUpdate}
          onDelete={handleDeleteWorkflow}
        />
      )}
    </div>
  );
};

// Workflow Detail Modal Component
interface WorkflowDetailModalProps {
  workflow: Workflow;
  onClose: () => void;
  onUpdate: () => void;
}

const WorkflowDetailModal: React.FC<WorkflowDetailModalProps> = ({
  workflow,
  onClose,
  onUpdate,
}) => {
  return (
    <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50">
      <div className="relative top-20 mx-auto p-5 border w-11/12 max-w-4xl shadow-lg rounded-md bg-white">
        {/* Header */}
        <div className="flex items-center justify-between mb-6">
          <div>
            <h3 className="text-lg font-medium text-gray-900">
              {workflow.name}
            </h3>
            <p className="text-sm text-gray-500">{workflow.description}</p>
          </div>
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-gray-600"
          >
            <span className="sr-only">Close</span>
            <svg
              className="h-6 w-6"
              fill="none"
              viewBox="0 0 24 24"
              stroke="currentColor"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M6 18L18 6M6 6l12 12"
              />
            </svg>
          </button>
        </div>

        {/* Content */}
        <div className="space-y-6">
          {/* Status and Stats */}
          <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
            <div className="bg-gray-50 p-4 rounded-lg">
              <div className="text-sm font-medium text-gray-500">Status</div>
              <div
                className={`mt-1 inline-flex px-2 py-1 text-xs font-semibold rounded-full ${
                  workflow.status === "active"
                    ? "bg-green-100 text-green-800"
                    : "bg-gray-100 text-gray-800"
                }`}
              >
                {workflow.status}
              </div>
            </div>
            <div className="bg-gray-50 p-4 rounded-lg">
              <div className="text-sm font-medium text-gray-500">
                Total Executions
              </div>
              <div className="mt-1 text-2xl font-semibold text-gray-900">
                {workflow.execution_count}
              </div>
            </div>
            <div className="bg-gray-50 p-4 rounded-lg">
              <div className="text-sm font-medium text-gray-500">
                Successful
              </div>
              <div className="mt-1 text-2xl font-semibold text-green-600">
                {workflow.success_count}
              </div>
            </div>
            <div className="bg-gray-50 p-4 rounded-lg">
              <div className="text-sm font-medium text-gray-500">
                Success Rate
              </div>
              <div className="mt-1 text-2xl font-semibold text-blue-600">
                {workflow.execution_count > 0
                  ? Math.round(
                      (workflow.success_count / workflow.execution_count) * 100
                    )
                  : 0}
                %
              </div>
            </div>
          </div>

          {/* Workflow Steps */}
          <div>
            <h4 className="text-md font-medium text-gray-900 mb-3">
              Workflow Steps
            </h4>
            <div className="space-y-3">
              {workflow.config.steps.map((step, index) => (
                <div
                  key={step.id}
                  className="flex items-center space-x-3 p-3 bg-gray-50 rounded-lg"
                >
                  <div className="flex items-center justify-center w-8 h-8 bg-indigo-100 text-indigo-600 rounded-full font-medium text-sm">
                    {index + 1}
                  </div>
                  <div className="flex-1">
                    <div className="text-sm font-medium text-gray-900">
                      {step.name}
                    </div>
                    <div className="text-xs text-gray-500">
                      {step.service} • {step.action}
                    </div>
                  </div>
                  <div className="text-xs text-gray-400 capitalize">
                    {step.type}
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* Variables */}
          {workflow.config.variables.length > 0 && (
            <div>
              <h4 className="text-md font-medium text-gray-900 mb-3">
                Configuration Variables
              </h4>
              <div className="space-y-2">
                {workflow.config.variables.map((variable) => (
                  <div
                    key={variable.name}
                    className="flex items-center justify-between p-3 bg-gray-50 rounded-lg"
                  >
                    <div>
                      <div className="text-sm font-medium text-gray-900">
                        {variable.name}
                      </div>
                      <div className="text-xs text-gray-500">
                        {variable.description}
                      </div>
                    </div>
                    <div className="text-sm text-gray-600">
                      {variable.currentValue ||
                        variable.defaultValue ||
                        "Not set"}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="mt-6 flex items-center justify-end space-x-3">
          <button
            onClick={onClose}
            className="px-4 py-2 border border-gray-300 rounded-md text-sm font-medium text-gray-700 hover:bg-gray-50"
          >
            Close
          </button>
          <button
            onClick={() => {
              // Handle execute
              console.log("Execute workflow from modal");
            }}
            className="px-4 py-2 bg-green-600 text-white text-sm font-medium rounded-md hover:bg-green-700"
          >
            Execute Now
          </button>
        </div>
      </div>
    </div>
  );
};

export default WorkflowList;
